import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { get } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { SetCamera, SetZoomed } from 'src/app/engine/state/camera/engine.camera.actions';
import { EngineCameraState } from 'src/app/engine/state/camera/engine.camera.state';
import { FlyToObject } from 'src/app/engine/state/engine.actions';
import { LabelsService } from 'src/app/story/labels.service';
import { ErrandType } from 'src/app/story/models/errands/errand-type.enum';
import { ErrandInterface } from 'src/app/story/models/errands/errand.model';
import { LabelCollapseModeEnum } from 'src/app/story/models/labels/label-collapse-mode';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { SelectLabel, SetLabel, SetLabelOffset } from 'src/app/story/state/labels/story.labels.actions';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';

@Component({
  selector: 'app-story-label',
  templateUrl: './story-label.component.html',
  styleUrls: ['./story-label.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoryLabelComponent implements OnInit, OnDestroy {
  @Input()
  label: LabelModel;

  @Input()
  emitEvents = true;

  @Input()
  collapsedMode: LabelCollapseModeEnum = LabelCollapseModeEnum.None;
  
  @Input()
  collapsed = false;

  @Input()
  pinDraggable = false;

  @Input()
  tagDraggable = false;

  @Select(StoryLabelsState.getLabelScale)
  labelScale$: Observable<number>;

  @Select(StoryLabelsState.getSelectedLabel)
  selectedLabel$: Observable<LabelModel>;

  // Local expanded / collapsed references
  descriptionCollapsed = false;
  tagCollapsed = false;

  get isCollapsed(): boolean {
    if (this.collapsedMode === LabelCollapseModeEnum.Toggle) {
      const isSelected = this.store.selectSnapshot(StoryLabelsState.getSelectedLabelId) === this.label.id;
      return this.collapsed && !isSelected;
    } else {
      return this.collapsed;
    }
  }

  sequence: number | string;
  
  private ngUnsubscribe = new Subject();

  get hasContent() {
    return this.labelsService.hasContent(this.label);
  }

  constructor(
    private store: Store,
    private labelsService: LabelsService,
    private engineService: EngineService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.selectedLabel$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((label) => this.handleLabelSelected(label));

    this.store
      .select(StoryLabelsState.getChapterLabels)
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(100))
      .subscribe(() => {
        this.sequence = this.store.selectSnapshot(
          StoryLabelsState.getLabelSequence(this.label)
        );
        this.changeDetectorRef.markForCheck();
      });
  

    this.descriptionCollapsed = this.label.collapseDescription;
    this.tagCollapsed = this.label.hideTag;
  }

  ngOnDestroy() {
    // Unsubscribe
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleLabelUpdated($event) {
    const {label, shouldPreserve} = $event;
    this.store.dispatch(new SetLabel(label, shouldPreserve));
  }

  handleOffsetUpdated($event) {
    const {labelId, offset} = $event;
    this.store.dispatch(new SetLabelOffset(labelId, offset));
  }

  handlePinClicked($event) {
    this.selectLabel();
  }

  handleTagClicked($event) {
    this.selectLabel();
  }

  private selectLabel() {
    const previousLabel = this.store.selectSnapshot(
      StoryLabelsState.getSelectedLabel
    );
    // Clicking same label to unselect
    if (previousLabel && previousLabel.id === this.label.id) {
      this.store.dispatch(new SelectLabel(null));
    } else {
      this.store.dispatch(new SelectLabel(this.label));
    }

    this.emitEngineEvent(EventsEnum.HotspotsPicked, { id: this.label.id });
    this.emitEngineEvent(EventsEnum.LabelsPicked, { labelId: this.label.id });
  }

  private emitEngineEvent(event: EventsEnum, params: any) {
    if (this.emitEvents) {
      this.engineService.fireEvent(event, params);
    }
  }

  handleDisplayUpdated($event) {
    this.emitEngineEvent(EventsEnum.HotspotsDisplayUpdated, $event);
  }

  handlePositionUpdated($event) {
    this.emitEngineEvent(EventsEnum.HotspotsPositionUpdated, $event.positionUpdate);
    this.emitEngineEvent(EventsEnum.LabelsMoved, {
      labelId: $event.positionUpdate.id,
      canvasPosition: [ $event.positionUpdate.canvasPos.x, $event.positionUpdate.canvasPos.y ],
      occluded: $event.positionUpdate.occluded || !$event.onCanvas,
    });
  }

  handleHoverStart($event) {
    this.emitEngineEvent(EventsEnum.HotspotsEntered, {id: $event.labelId});
    this.emitEngineEvent(EventsEnum.LabelsEntered, {labelId: $event.labelId});
  }

  handleHoverEnd($event) {
    this.emitEngineEvent(EventsEnum.HotspotsLeft, {id: $event.labelId});
    this.emitEngineEvent(EventsEnum.LabelsLeft, {labelId: $event.labelId});
  }


  handleLabelSelected(label: LabelModel) {
    const isSelected = label && label.id === this.label.id;
    const errands = <ErrandInterface[]>get(label, 'states[1].errands');
    const cameraLocked = this.store.selectSnapshot(
      EngineCameraState.getLockCamera
    );

    // Fly to
    if (isSelected && errands && !cameraLocked) {
      errands.forEach((errand) => {
        switch (errand.type) {
          case ErrandType.flyToObject:
            this.store.dispatch(new FlyToObject(errand.value));
            break;

          case ErrandType.flyToCamera:
            this.store.dispatch(new SetCamera(errand.value));
            break;
        }

        this.store.dispatch(new SetZoomed(true));
      });
    }

    // Expand
    const tagEnabled = !!this.label.title || this.hasContent;
    if (!tagEnabled) {
      this.tagCollapsed = true;
    } else if (this.label.expandTag && this.label.hideTag) {
      this.tagCollapsed = !isSelected;
    } else {
      this.tagCollapsed = this.label.hideTag;
    }

    // Show description
    if (this.label.showDescription && this.label.collapseDescription) {
      this.descriptionCollapsed = !isSelected;
    } else {
      this.descriptionCollapsed = this.label.collapseDescription;
    }
  }

  // toggleDescription(open?: boolean) {
  //   open = (typeof open === 'undefined') ? !this.descriptionCollapsed : open;
  //   this.descriptionCollapsed = open;
  // }
}
